import { Stack, useMediaQuery, useTheme } from "@mui/material";
import { useEffect, useState } from "react";
import { CircularLoading } from "../../../../../components/common";
import { useSessionContext } from "../../../../../contexts/SessionContext";
import { BundlePatientSummary } from "../../../../../services/fhir";
import { useFhirData } from "../../../../../utils/useFhirData";
import { useDocumentDialog } from "../../../../../hooks/useDocumentDialog";
import {
  processMedicalNotes,
  processSolicitations,
  processNoteReports,
} from "../../../../../hooks/useDocumentProcessing";
import { IframeDialog } from "../../../../ehrrecords/components/IframeDialog";
import { EhrRecordsLayoutProvider } from "../../../../ehrrecords/context/EhrRecordsLayoutContext";
import { MedicalNotes } from "../../../../ehrrecords/components/patientSummaryAccordions/Documents/components/MedicalNotes";
import { NoteReport } from "../../../../ehrrecords/components/patientSummaryAccordions/Documents/components/NoteReport";
import { Solicitations } from "../../../../ehrrecords/components/patientSummaryAccordions/Documents/components/Solicitations";
import { ProceduresMobile } from "./ProceduresMobile";
import { MyfamilyForDash } from "@/pages/myfamily/MyfamilyForDash";

export function ViewDocs({ type, sign = true }: any) {
  const { user } = useSessionContext();
  const theme = useTheme();
  const isMobileLayout = useMediaQuery(theme.breakpoints.down("md"));

  // Estados para gerenciar carregamento e dados
  const [loading, setLoading] = useState<boolean>(true);
  const [summaryData, setSummaryData] = useState();
  const [refetch, setRefetch] = useState<boolean>(false);

  // Gerencia estado do dialog de visualização usando hook reutilizável
  const { open, binaryUrl, setBinaryUrl, handleOpen, handleClose } =
    useDocumentDialog();

  // Extrai os dados do summaryData usando useFhirData
  const medicalNoteData = useFhirData.extractPatientSummaryResource(
    summaryData,
    "DocumentReference",
    "prescricao",
  )?.[0]?.entry;

  const solicitationData = useFhirData.extractPatientSummaryResource(
    summaryData,
    "DocumentReference",
    "service-request",
  )?.[0]?.entry;

  const NoteReportData = useFhirData.extractPatientSummaryResource(
    summaryData,
    "DocumentReference",
    "atestado",
  )?.[0]?.entry;

  const resultsData = useFhirData.extractPatientSummaryResource(
    summaryData,
    "DocumentReference",
    "resultado",
  );

  // Extrai MedicationRequest para processar descrições de prescrições
  const medicationRequest = useFhirData.extractPatientSummaryResource(
    summaryData,
    "MedicationRequest",
  )?.[0]?.entry;

  // Extrai ServiceRequest para processar descrições de solicitações
  const serviceRequest = useFhirData.extractPatientSummaryResource(
    summaryData,
    "ServiceRequest",
  )?.[0]?.entry;

  // Processa dados usando funções utilitárias reutilizáveis
  const rowData = processMedicalNotes(
    medicalNoteData,
    medicationRequest,
    sign,
  );

  const rows = processSolicitations(
    solicitationData,
    serviceRequest,
    resultsData,
    sign,
  );

  const noteReportRowData = processNoteReports(NoteReportData, sign);

  // Carrega dados do paciente quando o componente é montado ou quando refetch muda
  useEffect(() => {
    async function GetBundle() {
      setLoading(true);
      try {
        const bundleResponse = await BundlePatientSummary({
          identifier: user?.username,
          purposeofuse: "Atendimento",
        });

        setSummaryData(bundleResponse);
      } catch (err) {
        console.log("PatientSummary error:", err);
      } finally {
        setLoading(false);
      }
    }
    if (user?.username) GetBundle();
  }, [user, refetch]);

  return (
    <EhrRecordsLayoutProvider isMobileLayout={isMobileLayout}>
      <Stack
        sx={{
          padding: isMobileLayout ? "4px 0 0 0" : "20px 10px",
          width: "100%",
          height: "100%",
          maxHeight: isMobileLayout ? window.innerHeight - 150 : "100%",
        }}
      >
        {loading ? (
          <Stack sx={{ height: "100%" }}>
            <CircularLoading />
          </Stack>
        ) : (
          <>
            {type === "Notes" && (
              <MedicalNotes
                sign={sign}
                rowData={rowData}
                medicationRequest={medicationRequest}
                handleOpen={handleOpen}
                setBinaryUrl={setBinaryUrl}
              />
            )}

            {type === "Exames" && (
              <Solicitations
                setRefetch={setRefetch}
                sign={sign}
                handleOpen={handleOpen}
                setBinaryUrl={setBinaryUrl}
                rowData={rows}
                resultsData={resultsData}
              />
            )}

            {type === "Certificates" && (
              <NoteReport
                sign={sign}
                rowData={noteReportRowData}
                handleOpen={handleOpen}
                setBinaryUrl={setBinaryUrl}
              />
            )}

            {type === "Procedures" && (
              <ProceduresMobile
                handleOpen={handleOpen}
                setBinaryUrl={setBinaryUrl}
                summaryData={summaryData}
                setRefetch={setRefetch}
              />
            )}

            {type === "MyFamily" && <MyfamilyForDash />}

            {open && (
              <IframeDialog
                handleClose={handleClose}
                isOpen={open}
                data={binaryUrl}
              />
            )}
          </>
        )}
      </Stack>
    </EhrRecordsLayoutProvider>
  );
}
